import React, { forwardRef, useState } from "react";
import { Input, InputProps } from "@/shared/ui/Input";
import { Label } from "@/shared/ui/Label";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { ErrorText, SubtitleText } from "@/shared/ui/Typography";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import { cn } from "@/shared/utils/shadcn";

interface InputWithLabelProps extends InputProps {
  label?: string;
  description?: string;
  error?: string;
  rightElement?: React.ReactNode;
}

export const InputWithLabel = forwardRef<HTMLInputElement, InputWithLabelProps>(
  (props, ref) => {
    const { label, id, description, ...rest } = props;
    return (
      <div className="grid w-full items-center gap-2">
        {(!!label || !!description) && (
          <Column>
            {label && <Label htmlFor={id}>{label}</Label>}
            {description && <SubtitleText>{description}</SubtitleText>}
          </Column>
        )}
        <Row className="w-full items-center relative group">
          <Input id={id} ref={ref} {...rest} />
          {props.rightElement && (
            <Column className="absolute right-2 hidden group-hover:inline-flex">
              {props.rightElement}
            </Column>
          )}
        </Row>
        {props.error && (
          <ErrorText className="border-l-0 px-0">{props.error}</ErrorText>
        )}
      </div>
    );
  }
);

export const PasswordInput = forwardRef<HTMLInputElement, InputWithLabelProps>(
  (props, ref) => {
    const [showPassword, setShowPassword] = useState(false);

    const PasswordIcon = showPassword ? "Unlocked" : "Locked";

    return (
      <InputWithLabel
        {...props}
        ref={ref}
        type={showPassword ? "text" : "password"}
        rightElement={
          <Icon
            size="sm"
            color="grey"
            className="cursor-pointer"
            icon={PasswordIcon}
            onClick={() => setShowPassword(!showPassword)}
          />
        }
      />
    );
  }
);

export const PhoneNumberInput = forwardRef<
  HTMLInputElement,
  InputWithLabelProps
>((props, ref) => {
  const { label, id, ...rest } = props;

  return (
    <div className="grid w-full items-center gap-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      {/* @ts-ignore */}
      <PhoneInput
        id={id}
        ref={ref as any}
        countrySelectProps={{ unicodeFlags: true }}
        defaultCountry="EE"
        placeholder="Enter phone number"
        inputComponent={Input}
        {...rest}
      />
    </div>
  );
});
